// [uTheory](http://uTheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Draw a note on the staff.
//
// For training recognition of particular notes, as well as for other
// exercises that simply require

import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import { clefNames, isClefName } from 'shared/definitions/musicTheory/clefs';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { sample } from 'shared/src/utils/sample';
import { makeString } from 'shared/src/knowledge';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';
import QuestionGenerator from '../uTheory.Skills/questionGenerator.js';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import utils from '../utils';
import Note from '../uTheory.Notation/note';
import Clef from '../uTheory.Notation/clef';
import Staff from '../uTheory.Notation/staff';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Editable from '../uTheory.Notation/editable';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import Music from '../uTheory.Notation/music';
import KeySignature from '../uTheory.Notation/keySignature';
import type { InteractiveRegistryEntry } from '../uTheory.Skills/interactiveRegistry';

export interface NoteDrawOptions extends InteractiveOptions {
  ignoreOctave?: boolean;
  clef?: ClefName;
  clefs?: ClefName[];
  questions?: (string | Note)[];
}

interface NoteDrawQuestion {
  text: [string, number];
  note: Note;
  skills: (string | number)[];
  clef: Clef;
}

export default class NoteDraw extends InteractivePrototype {
  declare options: NoteDrawOptions;

  declare questions: NoteDrawQuestion[];

  declare currentQuestion: NoteDrawQuestion;

  clef!: Clef;

  noteHolder!: NoteHolder;

  skillCalculator: SkillCalculator | null;

  // Public constructor
  constructor(displayDiv: HTMLDivElement, options: NoteDrawOptions) {
    super(displayDiv, options);
    this.settings = {
      instructions: true,
      editor: {
        fixedWidth: 110,
        ariaTitle: () => {
          if (!this.music) return 'notation';
          const clef = this.music.voices[0]!.getStaff().clef;
          const note = this.music.voices[0]!.measures[0]!.noteHolders[0]!.notes[0];
          if (!note) return 'notation';
          const lineSpaceString = clef.toLineSpaceString(note);
          return `${clef.toString()} clef: ${lineSpaceString}`;
        },
        ariaLive: () =>
          this?.music?.voices[0]?.measures[0]?.noteHolders[0]?.notes[0] ? 'off' : 'polite',
        disableTextInput: true,
        maxLegerLines: 3,
      },
      letters: {
        letterButtons: false,
        checkButton: true,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      oneTryOnly: false,
    };

    this.skillCalculator = null;
    this.targetSpeedFromSkills = NoteDraw.registry[0]!.skills;
    this.init(displayDiv, options);
  }

  // Overriding these methods / properties:
  init(displayDiv: HTMLDivElement, options: NoteDrawOptions) {
    options.maxQuestions = options.drill ? options.maxQuestions || 12 : options.maxQuestions;
    if (!options.clefs && options.clef) {
      options.clefs = [options.clef];
    } else {
      options.clefs = undefined;
    }

    if (!options.drill && !options.testMode && (!options.questions || !options.questions.length)) {
      throw new Error(
        "Can't find a set of questions on the options object, but NoteDraw is not in drill or test mode."
      );
    }

    if (typeof options.clef === 'undefined' && options.drill) {
      const findSkill = function (str: string) {
        return !!options?.skills?.find((skill) => makeString(skill)?.search(str) !== -1);
      };
      options.clefs = [];
      if (findSkill('treble')) options.clefs.push('treble');
      if (findSkill('bass')) options.clefs.push('bass');
    }

    // now call the prototype's init function:
    return super.init(displayDiv, options);
  }

  generateQuestions() {
    this.questions = this.options.drill
      ? this.generateDrillQuestions()
      : this.processManualQuestions();

    const totalQuestions = this.questions.length;

    this.questionPrompt = this.options.questionPrompt;
    if (typeof this.questionPrompt === 'undefined') {
      this.questionPrompt = `Draw the note <span aria-live="assertive">{question}</span> on the staff. {question} of ${totalQuestions}`;
    }
  }

  generateDrillQuestions(): NoteDrawQuestion[] {
    return this.randomQuestionGenerator().map((note, i) => this.processQuestion(note, i));
  }

  processManualQuestions(questions = this.options.questions): NoteDrawQuestion[] {
    if (!questions) {
      throw Error('Cannot run noteDraw in manual mode without specified questions.');
    }
    return questions.map((q, i) => this.processQuestion(q, i));
  }

  processQuestion(questionStringOrNote: string | Note, i: number): NoteDrawQuestion {
    if (typeof questionStringOrNote !== 'string' && !(questionStringOrNote instanceof Note))
      throw Error('Only string or Note questions are allowed for noteDraw.');

    const note =
      questionStringOrNote instanceof Note
        ? questionStringOrNote
        : new Note(questionStringOrNote.split('/')[0] as NoteName);

    // Does the question string have a clef in it?
    const clefName =
      typeof questionStringOrNote === 'string' && isClefName(questionStringOrNote.split('/')[1])
        ? (questionStringOrNote.split('/')[1] as ClefName)
        : assignClefToNoteFromNoteAndSkillsOrClefOptions({
            note,
            skillIds: this.options.skills,
            allowedClefs: this.options.clefs || [this.options.clef],
          });
    // sample(note.prefersClef(this.options.clefs));
    const clef = new Clef(clefName);

    const noteHTML = this.options.ignoreOctave
      ? note.toAccidentalFontShortString()
      : note.toAccidentalFontString();

    const skillCalculatorClosure = () => this.calculateSkill();

    return {
      note,
      text: [noteHTML, i + 1],
      clef,
      get skills() {
        return skillCalculatorClosure();
      },
    };
  }

  initializeMusic() {
    // In case someone forgot to set it, assume treble.
    const clefName = this.options?.clefs?.[0] || 'treble';
    if (!isClefName(clefName)) {
      throw Error(`${clefName} is not a valid clef name.`);
    }
    const clef = new Clef(clefName);
    this.clef = clef;
    const staff = new Staff(clef);
    this.noteHolder = new NoteHolder('', 'w');
    this.noteHolder.editable = new Editable(this.noteHolder, {
      pitch: true,
      accidental: false,
      deleteable: false,
      selectable: true,
      highestLine: 8,
      lowestLine: -2,
    });
    const measure = new Measure(this.noteHolder);

    const voice = new Voice(measure);
    voice.setStaff(staff);

    const keySig = new KeySignature('C', 'Major');
    this.music = new Music(staff, voice, keySig);
  }

  randomQuestionGenerator() {
    const generator = new QuestionGenerator();

    const skills = this.options.skills || [];
    const skillObjects: { skill: string; probability: number }[] = [];

    const skillStrings = skills.map(makeString).filter((s): s is string => !!s);
    // If we have an octave_numbers skill, but not clef skill, add the related cleff skills:
    if (skillStrings.length === 1 && skillStrings.includes('treble_clef_octave_numbers')) {
      skillStrings.push('treble_c_g', 'treble_a_g', 'treble_low_legers', 'treble_high_legers');
    }
    if (skillStrings.length === 1 && skillStrings.includes('bass_clef_octave_numbers')) {
      skillStrings.push('bass_lines', 'bass_spaces', 'bass_high_legers', 'bass_low_legers');
    }

    skillStrings
      .filter(
        (skillString) =>
          skillString !== 'treble_clef_octave_numbers' && skillString !== 'bass_clef_octave_numbers'
      )
      .forEach((skillString) => {
        let probability;
        switch (skillString) {
          case 'treble_c_g':
            probability = 5;
            break;
          case 'treble_a_g':
            probability = 7;
            break;
          case 'treble_low_legers':
            probability = 4;
            break;
          case 'treble_high_legers':
            probability = 3;
            break;
          case 'bass_lines':
            probability = 5;
            break;
          case 'bass_spaces':
            probability = 4;
            break;
          case 'bass_high_legers':
            probability = 4;
            break;
          case 'bass_low_legers':
            probability = 4;
            break;
          default:
            probability = 7;
            break;
        }
        skillObjects.push({ skill: skillString, probability });
      }, this);

    return generator.multipleSkillsGenerate(
      skillObjects,
      this.options.maxQuestions,
      this.options
    ) as Note[];
  }

  addListeners() {
    this.letterPanel!.addCheckCallback(this.check, this);
  }

  calculateSkill(note = this.noteHolder?.notes[0]): (string | number)[] {
    if (!note) {
      // It might be empty -- i.e., test mode
      note = this.currentQuestion.note;
    }
    if (!this.skillCalculator) {
      this.skillCalculator = new SkillCalculator();
    }
    const skills: (string | number)[] = [this.skillCalculator.getClefSkill(note, this.clef)];
    if (!this.options.ignoreOctave) {
      switch (this.currentQuestion.clef.toString()) {
        case 'treble':
          skills.push('treble_clef_octave_numbers');
          break;
        case 'bass':
          skills.push('bass_clef_octave_numbers');
          break;
        // default:
        //   skills.push('c_clef_octave_numbers');
      }
    }
    return skills;
  }

  check() {
    if (this.rightAnswerDelay) return;
    if (this.optionsTrue('testMode')) {
      this.checkpointAnswer();
      return;
    }

    if (this.noteHolder.length === 0) {
      this.checkMark!.warn('Draw a note first.');
      return;
    }

    const answerNote = this.noteHolder.notes[0];
    const expectedNote = this.questions[this.questionNumber]!.note;
    let correct = answerNote!.letter === expectedNote.letter;
    if (this.options.ignoreOctave !== true) {
      correct = correct && answerNote!.octave === expectedNote.octave;
    }

    if (correct) {
      this.rightAnswer(1000);
    } else {
      this.wrongAnswer();
    }
  }

  newQuestion() {
    this.noteHolder.length = 0;
    this.noteHolder.editable!.selected = false;
    super.newQuestion();
    this.clef.setName(this.currentQuestion.clef);
    this.editor!.renderer.render();
  }

  // CheckPoint Mode Questions:
  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      this.noteHolder.remove(this.noteHolder.notes[0]!);
      this.noteHolder.editable!.selected = false;
      this.noteHolder.editable!.pendingNote = false;

      const question = this.processQuestion(questionString, 0);
      const { clef } = question;
      this.clef.setName(clef);

      this.editor!.renderer.render();

      this.questions = [question];
      const aOrAn = utils.aOrAnNote(question.text[0]);

      const questionText = `Draw ${aOrAn} ${question.text[0]} on the staff below.`;
      this.instructionsDiv!.innerHTML = questionText;

      this.questions = [question];
      this.currentQuestion = question;

      return Promise.resolve(this.questions[0]);
    });
  }

  getAnswerAndData() {
    const answerNote = this.noteHolder.notes[0];
    const unanswered = typeof answerNote === 'undefined';
    const expectedNote = this.questions[0]!.note;
    let correct = !unanswered && answerNote.letter === expectedNote.letter;
    if (!this.optionsTrue('ignoreOctave')) {
      correct = correct && answerNote!.octave === expectedNote.octave;
    }

    const skill = correct ? this.calculateSkill() : this.calculateSkill(expectedNote);

    if (!correct) {
      this.noteHolder.color = 'red';
      this.editor!.renderer.render();
    }
    this.editor!.deselectAll();
    const SVG = this.editor!.renderer.exportSVG();
    if (!correct) {
      delete this.noteHolder.color;
      this.editor!.renderer.render();
    }

    const answerAndData = {
      question: this.questions[0]!.text[0],
      questionType: 'NoteDraw',
      questionText: this.instructionsDiv!.innerHTML,
      studentAnswerText: answerNote ? answerNote.toString() : '',
      studentAnswerSVG: SVG,
      correctAnswerText: expectedNote.toString(),
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: skill,
    };

    return answerAndData;
  }

  gameOver() {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    this.instructionsDiv!.setAttribute('role', 'alert');
    delete this.noteHolder.editable;
    this.editor!.renderer.render();
    this.displayDiv.removeChild(this.lettersDiv!);
    super.gameOver();
  }

  playAnswer() {
    this.player!.play(this.noteHolder, { startTime: 0, stopTime: 1.15 });
  }

  static registry: InteractiveRegistryEntry[] = [
    {
      skills: ['treble_c_g', 'treble_a_g', 'bass_lines', 'bass_spaces'],
      options: { ignoreOctave: true },
      description: 'Draw Notes on the Staff',
    },
    {
      skills: ['treble_clef_octave_numbers', 'bass_clef_octave_numbers'],
      alsoTrains: ['treble_c_g', 'treble_a_g', 'bass_lines', 'bass_spaces'],
      options: { ignoreOctave: false },
      description: 'Draw Notes in Specific Octaves',
    },
  ];
}

function assignClefToNoteFromNoteAndSkillsOrClefOptions({
  note,
  skillIds,
  allowedClefs,
}: {
  note: Note;
  skillIds?: (number | string)[];
  allowedClefs?: ClefName[];
}) {
  if (allowedClefs?.length) {
    const potentialClefs = note.prefersClef(allowedClefs, 3).length
      ? note.prefersClef(allowedClefs, 3)
      : note.prefersClef();

    return sample(potentialClefs);
  }

  if (skillIds?.length) {
    const skills = skillIds.map(makeString);
    const validClefs = clefNames.filter((clef) =>
      skills
        // The mezzo_soprano_clef skill needs to be converted to mezzo-soprano to match clef names:
        .map((skill) => skill.replace(/_/g, '-'))
        // Once that's done, twe can find any clef name in the skill name:
        .find((skill) => {
          if (skill.includes('soprano')) {
            if (skill.includes('mezzo-soprano')) {
              return clef === 'mezzo-soprano';
            }
            return clef === 'soprano';
          }
          return skill.includes(clef);
        })
    );
    if (validClefs.length === 0) {
      validClefs.push('treble', 'bass');
    }
    const potentialClefs = note.prefersClef(validClefs, 3).length
      ? note.prefersClef(validClefs, 3)
      : note.prefersClef();

    return sample(potentialClefs);
  }

  return sample(note.prefersClef());
}
